import tenantConstants from '@constants';
import { isGCCTenant } from '../../../utility/env';
import { isLandCategorySlug } from './listingCategories/common/landCategory';

export const FORM_KEYS = {
  purpose: 'purpose',
  type: 'property_type',
  location: 'location_select',
  area: 'area',
  price: 'price',
  hasInstallments: 'installment_available',
  advanceAmount: 'advance_amount',
  monthlyInstallment: 'monthly_installment',
  noOfInstallments: 'no_of_installments',
  readyForPossession: 'ready_for_possession',
  bedrooms: 'bedrooms',
  bathrooms: 'bathrooms',
  features: 'feature_and_amenities',
  title: 'property_title',
  description: 'property_description',
  images: 'property_images',
  email: 'email',
  mobileA: 'mobileA',
  mobileB: 'mobileB',
  platform: 'platform_selection',
};

export const listingTypeIds = {
  Land: 42,
  Building: 43,
};

export const propertyTypes = {
  [listingTypeIds.Land]: [
    { id: 20, label: 'Industrial Land' },
    { id: 19, label: 'Commercial Land' },
    { id: 9, label: 'Residential Land' },
  ],
  [listingTypeIds.Building]: [
    { id: 8, label: 'Residential Building' },
    { id: 17, label: 'Commercial Building' },
  ],
};

/**
 * Surge `listing_categories[].external_id` — canonical property-type ids. The per-tenant primary
 * `id` differs across tenants, but `external_id` is the shared canonical scheme, and sale/rent use
 * the SAME external_id for a given property type (e.g. Apartment = 3 for both sale and rent).
 * These are the property types that offer the Studio (-1) bedroom option: Apartment and Chalet.
 */
export const COMMON_PROPERTY_TYPE_STUDIO_BEDROOM_EXTERNAL_IDS = [
  3, // Apartment
  5, // Chalet
];

export const getSubPropertyTypes = (typeId) => {
  let propertyType;
  if (propertyTypes?.[typeId]) {
    propertyType = propertyTypes?.[typeId];
  } else {
    Object.keys(propertyTypes).forEach((key) => {
      const foundItem = propertyTypes[key].find((item) => item.id === typeId);
      if (foundItem) {
        propertyType = propertyTypes[key];
      }
    });
  }
  return propertyType;
};

/**
 * Keyed by Surge `listing_categories[].external_id` (canonical property-type id, shared across
 * tenants; sale and rent use the SAME external_id). Only entries that differ from the default
 * need to be listed — an absent property type defaults to showing the field (residential default),
 * matching `main`. Canonical residential units (Apartment 3, Villa 4, Chalet 5) keep beds/baths/
 * furnished/features; land, garage and commercial types (7, 8, 9, 10) hide them.
 */
export const skipFieldsForField = {
  bedrooms: {
    property_type: {
      // Residential units — show bedrooms (Studio handled via COMMON_PROPERTY_TYPE_STUDIO_BEDROOM_EXTERNAL_IDS)
      3: { skipField: false, isOptional: false }, // Apartment
      4: { skipField: false, isOptional: false }, // Villa
      5: { skipField: false, isOptional: false }, // Chalet
      // Land / garage / commercial — hide bedrooms
      7: { skipField: true, isOptional: true }, // Residential Land
      8: { skipField: true, isOptional: true }, // Garage
      9: { skipField: true, isOptional: true }, // Commercial Building
      10: { skipField: true, isOptional: true }, // Other Commercial
      ...(tenantConstants.ROOM_PROPERTY_TYPE_ENABLED && {
        63: { skipField: true, isOptional: true }, // Room (single-room rental — no bedroom count)
      }),
    },
  },
  bathrooms: {
    property_type: {
      3: { skipField: false, isOptional: false }, // Apartment
      4: { skipField: false, isOptional: false }, // Villa
      5: { skipField: false, isOptional: false }, // Chalet
      7: { skipField: true, isOptional: true }, // Residential Land
      8: { skipField: true, isOptional: true }, // Garage
      9: { skipField: true, isOptional: true }, // Commercial Building
      10: { skipField: true, isOptional: true }, // Other Commercial
      ...(tenantConstants.ROOM_PROPERTY_TYPE_ENABLED && {
        63: { skipField: false, isOptional: false }, // Room keeps bathrooms
      }),
    },
  },
  furnished: {
    property_type: {
      // Matches `main`: furnished is hidden only for land/garage/commercial and shown for every
      // other type (including the room type, which keeps furnished).
      7: { skipField: true, isOptional: true }, // Residential Land
      8: { skipField: true, isOptional: true }, // Garage
      9: { skipField: true, isOptional: true }, // Commercial Building
      10: { skipField: true, isOptional: true }, // Other Commercial
    },
  },
  feature_and_amenities: {
    property_type: {
      7: { skipField: true, isOptional: true }, // Residential Land
      8: { skipField: true, isOptional: true }, // Garage
      9: { skipField: true, isOptional: true }, // Commercial Building
      10: { skipField: true, isOptional: true }, // Other Commercial
      ...(tenantConstants.ROOM_PROPERTY_TYPE_ENABLED && {
        63: { skipField: true, isOptional: true }, // Room
      }),
    },
  },
};

export function shouldShowStudioBedroomOption(propertyTypeExternalId) {
  if (propertyTypeExternalId == null || propertyTypeExternalId === '') return false;
  const pt = Number(propertyTypeExternalId);
  if (Number.isNaN(pt)) return false;
  return COMMON_PROPERTY_TYPE_STUDIO_BEDROOM_EXTERNAL_IDS.includes(pt);
}

export function shouldHideBedroomsFieldForPropertyType(propertyTypeExternalId) {
  if (propertyTypeExternalId == null || propertyTypeExternalId === '') return false;
  const pt = Number(propertyTypeExternalId);
  if (Number.isNaN(pt)) return false;
  const rule = skipFieldsForField?.bedrooms?.property_type?.[pt];
  return rule === true || (rule && rule.skipField === true);
}

/**
 * Slug-based field visibility for the common (Surge) tenants — Oman, Jordan, Bahrain, etc.
 *
 * Why slug and not `external_id`: Surge assigns a DIFFERENT `listing_categories[].external_id` to
 * the same property type under sale vs rent (e.g. Chalet = 11 for sale, 58 for rent; Apartment =
 * 3 / 50), so a single numeric `property_type` map (`skipFieldsForField` above) cannot represent
 * both purposes and silently mismatches at runtime. The category `slug` is stable across purposes,
 * so we key off it here (mirrors `isLandCategorySlug` in listingCategories/common/landCategory.js).
 */
const normalizeSlug = (slug) => String(slug ?? '').toLowerCase().trim();

// Tolerant matchers — match on the stable slug stem so plural / variant spellings
// (e.g. `garage` vs `garages`) all resolve. Land is delegated to the shared
// `isLandCategorySlug` (covers residential/commercial/industrial land + plots), which is already
// proven in production (it drives completion-status hiding on land listings).
const isGarageCategorySlug = (s) => /garag/.test(s);
const isCommercialBuildingCategorySlug = (s) => /commercial-building/.test(s);
const isOtherCommercialCategorySlug = (s) => /commercial-(propert|other)/.test(s);
const isRoomCategorySlug = (s) => /(^|-)rooms?(-|$)/.test(s);
/** Property types that offer the Studio (-1) bedroom option: Apartment and Chalet. */
const isStudioBedroomCategorySlug = (s) => /(^|-)(apartment|chalet)/.test(s);

/**
 * Beds/baths are hidden — for BOTH sale and rent — for garage, any land (residential/commercial/
 * industrial), and both commercial building types. These property types have no bedroom/bathroom
 * concept. Purpose-independent (Oman product spec: garage + residential land hidden in sale & rent).
 */
export function shouldHideBedsBathsForCategorySlug(slug /* purposeSlug (unused — same for sale & rent) */) {
  const s = normalizeSlug(slug);
  if (!s) return false;
  return (
    isGarageCategorySlug(s) ||
    isLandCategorySlug(s) ||
    isCommercialBuildingCategorySlug(s) ||
    isOtherCommercialCategorySlug(s)
  );
}

/** Whether the Studio (-1) bedroom option should be offered for this category slug. */
export function shouldShowStudioBedroomOptionForCategorySlug(slug) {
  return isStudioBedroomCategorySlug(normalizeSlug(slug));
}

/**
 * Single source of truth for which dynamic form fields to omit for a property-type category on the
 * common (slug-based) tenants. `formKey` is the Formik field key (`bedrooms`, `bathrooms`,
 * `furnished`, `feature_and_amenities`).
 */
export function shouldOmitFieldForCategorySlug(formKey, slug, purposeSlug) {
  const s = normalizeSlug(slug);
  if (!s) return false;
  const isRoom = isRoomCategorySlug(s);
  const isLandOrCommercial =
    isGarageCategorySlug(s) ||
    isLandCategorySlug(s) ||
    isCommercialBuildingCategorySlug(s) ||
    isOtherCommercialCategorySlug(s);
  switch (formKey) {
    case 'bedrooms':
      if (isRoom) return true; // single-room rental — no bedroom count
      return shouldHideBedsBathsForCategorySlug(s, purposeSlug);
    case 'bathrooms':
      if (isRoom) return false; // Room keeps bathrooms (Oman)
      return shouldHideBedsBathsForCategorySlug(s, purposeSlug);
    case 'furnished':
      if (isRoom) return false; // Room keeps furnished
      return isLandOrCommercial;
    case 'feature_and_amenities':
      if (isRoom) return true;
      return isLandOrCommercial;
    default:
      return false;
  }
}

/**
 * Adapter so display surfaces can reuse the form's `shouldHideBedsBathsForCategorySlug` rule directly
 * from a listing object. It adds nothing to the rule itself — it only (a) pulls the category slug off
 * whichever shape the surface passes (raw/normalized listing carries `listing_type`/`listing_category`;
 * the table's `property` object carries `type`), and (b) scopes the gate to GCC tenants (oman, bahrain,
 * qatar, jordan, eg) so the shared card/table/drawer stay untouched for bayut, zameen and the rest.
 */
export function shouldHideBedsBathsForListing(listingLike) {
  if (!isGCCTenant || !listingLike) return false;
  const type = listingLike?.listing_type ?? listingLike?.type ?? listingLike?.listing_category;
  const slug = type?.slug ?? listingLike?.listing_category?.slug;
  return shouldHideBedsBathsForCategorySlug(slug);
}
